import { getTileModifierMeta } from '../boardConfig';

const toUpper = (value) => String(value || '').toUpperCase();

const EFFECT_REGISTRY = {
  GLOBAL_LETTER_BONUS: {
    getKey: (e) => `${e.type}|${toUpper(e.letter)}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      bonus: Number(existing.bonus || 0) + Number(next.bonus || 0),
    }),
  },
  GLOBAL_WORD_MULTIPLIER_FOR_LETTER: {
    getKey: (e) => `${e.type}|${toUpper(e.letter)}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      multiplier: Number(existing.multiplier || 1) * Number(next.multiplier || 1),
    }),
  },
  GLOBAL_ROUND_SUBMITS_BONUS: {
    getKey: (e) => `${e.type}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      bonusSubmits: Number(existing.bonusSubmits || 0) + Number(next.bonusSubmits || 0),
    }),
  },
  GLOBAL_ROUND_DISCARDS_BONUS: {
    getKey: (e) => `${e.type}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      bonusDiscards: Number(existing.bonusDiscards || 0) + Number(next.bonusDiscards || 0),
    }),
  },
  GLOBAL_SHOP_REROLLS_BONUS: {
    getKey: (e) => `${e.type}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      bonusRerolls: Number(existing.bonusRerolls || 0) + Number(next.bonusRerolls || 0),
    }),
  },
  GLOBAL_SHOP_PICKS_BONUS: {
    getKey: (e) => `${e.type}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      bonusPicks: Number(existing.bonusPicks || 0) + Number(next.bonusPicks || 0),
    }),
  },
  GLOBAL_SHOP_RARITY_CHANCE_BONUS: {
    getKey: (e) => `${e.type}|${toUpper(e.rarity)}|${String(e.scope || 'game')}`,
    merge: (existing, next) => ({
      ...existing,
      bonusChance: Number(existing.bonusChance || 0) + Number(next.bonusChance || 0),
    }),
  },
};

export const registerEffectType = (type, { getKey, merge }) => {
  EFFECT_REGISTRY[String(type).toUpperCase()] = { getKey, merge };
};

const getStackableEffectKey = (effect) =>
  EFFECT_REGISTRY[effect.type]?.getKey(effect) ?? null;

const mergeStackableEffects = (existingEffect, nextEffect) =>
  EFFECT_REGISTRY[existingEffect.type]?.merge(existingEffect, nextEffect) ?? existingEffect;

const buildEffectId = (effect, fallbackIndex = 0) => {
  const explicit = String(effect?.id || '').trim();
  if (explicit) {
    return explicit;
  }

  const type = String(effect?.type || 'EFFECT').toUpperCase();
  const keyMaterial = Object.entries(effect || {})
    .filter(([key]) => key !== 'id')
    .map(([key, value]) => `${key}:${String(value)}`)
    .join('|');

  return `${type}:${keyMaterial || fallbackIndex}`;
};

export const normalizeActiveGameEffects = (activeGameEffects = []) =>
  (Array.isArray(activeGameEffects) ? activeGameEffects : [])
    .filter(Boolean)
    .map((effect, index) => ({
      ...effect,
      type: String(effect.type || '').toUpperCase(),
      id: buildEffectId(effect, index + 1),
      scope: effect.scope || 'game',
    }))
    .filter((effect) => Boolean(effect.type))
    .reduce((acc, effect) => {
      const stackKey = getStackableEffectKey(effect);
      if (!stackKey) {
        acc.push(effect);
        return acc;
      }

      const existingIndex = acc.findIndex((entry) => getStackableEffectKey(entry) === stackKey);
      if (existingIndex < 0) {
        acc.push(effect);
        return acc;
      }

      const merged = mergeStackableEffects(acc[existingIndex], effect);
      acc[existingIndex] = {
        ...merged,
        id: buildEffectId(merged, existingIndex + 1),
      };

      return acc;
    }, []);

const effectLabel = (effect) => effect.label || effect.type;

const applyGlobalLetterBonus = (effect, letterContext) => {
  if (!letterContext) {
    return null;
  }

  const target = toUpper(effect.letter);
  if (!target || toUpper(letterContext.letter) !== target) {
    return null;
  }

  const bonus = Number(effect.bonus || 0);
  if (!bonus) {
    return null;
  }

  return {
    letterBonus: bonus,
    wordBonus: 0,
    wordMultiplier: 1,
    detail: {
      effectId: effect.id,
      label: effectLabel(effect),
      phase: 'letter',
      description: `+${bonus} on ${target}`,
      value: bonus,
    },
  };
};

const applyWordMultiplierForLetter = (effect, wordContext) => {
  if (!wordContext) {
    return null;
  }

  const target = toUpper(effect.letter);
  const lettersInWord = Array.isArray(wordContext.letters)
    ? wordContext.letters.map((letter) => toUpper(letter))
    : [];

  if (!target || !lettersInWord.includes(target)) {
    return null;
  }

  const multiplier = Number(effect.multiplier || 1);
  if (!Number.isFinite(multiplier) || multiplier === 1) {
    return null;
  }

  return {
    letterBonus: 0,
    wordBonus: 0,
    wordMultiplier: multiplier,
    detail: {
      effectId: effect.id,
      label: effectLabel(effect),
      phase: 'word',
      description: `Word x${multiplier} when ${target} is present`,
      value: multiplier,
    },
  };
};

const applyEffectByType = (effect, context) => {
  switch (effect.type) {
    case 'GLOBAL_LETTER_BONUS':
      return applyGlobalLetterBonus(effect, context.letterContext);
    case 'GLOBAL_WORD_MULTIPLIER_FOR_LETTER':
      return applyWordMultiplierForLetter(effect, context.wordContext);
    default:
      return null;
  }
};

export const applyLetterPhaseEffects = ({ activeGameEffects = [], letterContext }) => {
  const normalized = normalizeActiveGameEffects(activeGameEffects);

  return normalized.reduce(
    (acc, effect) => {
      const result = applyEffectByType(effect, { letterContext, wordContext: null });
      if (!result) {
        return acc;
      }

      return {
        letterBonus: acc.letterBonus + (result.letterBonus || 0),
        details: [...acc.details, result.detail],
      };
    },
    {
      letterBonus: 0,
      details: [],
    }
  );
};

export const applyWordPhaseEffects = ({ activeGameEffects = [], wordContext }) => {
  const normalized = normalizeActiveGameEffects(activeGameEffects);

  return normalized.reduce(
    (acc, effect) => {
      const result = applyEffectByType(effect, { letterContext: null, wordContext });
      if (!result) {
        return acc;
      }

      return {
        wordBonus: acc.wordBonus + (result.wordBonus || 0),
        wordMultiplier: acc.wordMultiplier * (result.wordMultiplier || 1),
        details: [...acc.details, result.detail],
      };
    },
    {
      wordBonus: 0,
      wordMultiplier: 1,
      details: [],
    }
  );
};

const normalizeGrantedEffects = (granted, context) =>
  (Array.isArray(granted) ? granted : [])
    .filter(Boolean)
    .map((effect, index) => ({
      ...effect,
      id: buildEffectId(
        {
          ...effect,
          sourceTileId: context.tileId,
          sourceModifierId: context.modifierId,
          grantSequence: index,
        },
        index + 1
      ),
      source: {
        tileId: context.tileId,
        letter: context.letter,
        modifierId: context.modifierId,
      },
      scope: effect.scope || 'game',
    }));

export const collectGrantedGameEffectsFromTiles = (tiles = [], options = {}) => {
  const runSettings = options?.runSettings;
  const rng = typeof options?.rng === 'function' ? options.rng : Math.random;
  const nextEffects = [];

  (Array.isArray(tiles) ? tiles : []).forEach((tile) => {
    const modifierIds = Array.isArray(tile?.modifiers) ? tile.modifiers : [];

    modifierIds.forEach((modifierId) => {
      const meta = getTileModifierMeta(modifierId);
      const rawGranted =
        typeof meta.grantsGameEffects === 'function'
          ? meta.grantsGameEffects({ tile, modifierId, runSettings, rng })
          : meta.grantsGameEffects;

      const normalized = normalizeGrantedEffects(rawGranted, {
        tileId: tile?.id || '',
        letter: toUpper(tile?.letter),
        modifierId,
      });

      normalized.forEach((effect) => {
        nextEffects.push(effect);
      });
    });
  });

  return nextEffects;
};